'use client';

import { DeliveryPlanType } from '@/core/entities/delivery-plan-type';

import { Tag, Tooltip } from 'antd';
import { CSSProperties } from 'react';

import { trackTooltipHover } from '@/core/utils/analytics.util';
import { DELIVERY_PLAN_DESCRIPTIONS } from '@/core/utils/delivery-plan.util';

import { deliveryPlanPillStyle } from './DeliveryPlanPillsStyle';

interface DeliveryPlanPillsProps {
  deliveryPlans: DeliveryPlanType[];
  style?: CSSProperties;
}

/**
 * Componente que muestra los planes de entrega como pills con iniciales.
 *
 * @param {DeliveryPlanPillsProps} props - Las propiedades del componente.
 *
 * @returns {JSX.Element} El componente renderizado.
 */
export default function DeliveryPlanPills({ deliveryPlans, style }: DeliveryPlanPillsProps) {
  if (!deliveryPlans || deliveryPlans.length === 0) {
    return <span style={style}>-</span>;
  }

  /**
   * Genera las iniciales de un plan de entrega.
   * Si el plan tiene un ID válido, usa el ID.
   * Si no, genera iniciales basadas en el nombre.
   */
  const getInitials = (plan: DeliveryPlanType): string => {
    if (plan.id && /^[A-Z]{2,3}$/.test(plan.id)) {
      return plan.id;
    }

    if (plan.name) {
      return plan.name
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase())
        .join('')
        .substring(0, 3);
    }

    return plan.id || 'N/A';
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', ...style }}>
      {deliveryPlans.map((plan, index) => {
        const initials = getInitials(plan);
        const description =
          DELIVERY_PLAN_DESCRIPTIONS[plan.id] || DELIVERY_PLAN_DESCRIPTIONS[initials] || plan.name;

        return (
          <Tooltip key={`${plan.id}-${index}`} title={description} placement='top'>
            <Tag
              style={deliveryPlanPillStyle}
              onMouseEnter={() => trackTooltipHover({ planCode: plan.id || initials })}>
              {initials}
            </Tag>
          </Tooltip>
        );
      })}
    </div>
  );
}
